<script lang="ts">import { cfgBrandTitle, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { isNotificationsOpen, isPlayerChatOpen } from "@infinitro/global";
import { PromotionsIcon } from "@infinitro/icons";
import { Text } from "@infinitro/text";
import { onDestroy, onMount } from "svelte";
import "svelte/store";
import PromoItemCustom from "./promo-item-custom.svelte";
import PromoItemMomentum from "./promo-item-momentum.svelte";
import PromoItem from "./promo-item.svelte";
const BRAND_NAME = cfgBrandTitle();
export let promotions;
export let initialCount = 9;
export let pageSize = 9;
$: useAlternative = $stateUi.promotions.promotionsStyle === "alternative";
$: useAlternative2 = $stateUi.promotions.promotionsStyle === "alternative2";
$: useMomentum = $stateUi.promotions.promotionsStyle === "momentum";
let visibleCount = initialCount;
$: allPromos = $promotions ?? [];
$: visiblePromos = allPromos.slice(0, visibleCount);
$: hasMore = visibleCount < allPromos.length;
let sentinel = null;
let io = null;
let loading = false;
onMount(() => {
	io = new IntersectionObserver((entries) => {
		const [entry] = entries;
		if (!entry.isIntersecting || loading) return;
		if (hasMore) {
			loading = true;
			visibleCount = Math.min(visibleCount + pageSize, allPromos.length);
			if (io && sentinel) {
				io.unobserve(sentinel);
				requestAnimationFrame(() => {
					if (sentinel) io.observe(sentinel);
					loading = false;
				});
			} else {
				loading = false;
			}
		}
	}, {
		root: null,
		rootMargin: "200px",
		threshold: 0
	});
	if (sentinel) io.observe(sentinel);
});
onDestroy(() => {
	io?.disconnect();
});
$: if (io && sentinel) {
	io.observe(sentinel);
}
$: if (visibleCount > allPromos.length) {
	visibleCount = Math.min(allPromos.length, Math.max(initialCount, visibleCount));
}
</script>

{#if useAlternative}
	<div class="circle circle-1"></div>
	<div class="circle circle-2"></div>
	<div class="circle circle-3"></div>
	<div class="circle circle-4"></div>
	<div class="circle circle-5"></div>
{/if}
<div class="all-promotions-wrapper" class:alternative-all-promotions-wrapper={useAlternative}>
	<div class="top-content">
		<div class="title-container">
			{#if !useAlternative && !useAlternative2 && !useMomentum}
				<PromotionsIcon className="title-icon" />
			{/if}
			<Text type="title" titleSize="h4">
				{$t(Verbiage.promotions)}
			</Text>
		</div>
		<div class="top-description">
			<Text type="body" bodySize="large" textColor={useAlternative2 ? 'white' : 'gray'}>
				{$t(Verbiage.welcome_to_the_pinnacle, 'global', {
					BRAND: useMomentum ? BRAND_NAME : BRAND_NAME?.toUpperCase()
				})}
			</Text>
		</div>
	</div>
	<div class="bottom-content">
		<div
			class="hot-promotions"
			class:hot-promotions-panels-open={$isPlayerChatOpen || $isNotificationsOpen}
		>
			{#each visiblePromos as promo (promo.id)}
				{#if useAlternative}
					<PromoItemCustom isDetail={false} {promo} highlight={true} isNew={!!promo.isNew} />
				{:else if useMomentum}
					<PromoItemMomentum {promo} compact />
				{:else}
					<PromoItem isDetail={false} {promo} highlight={true} isNew={!!promo.isNew} />
				{/if}
			{/each}

			{#if hasMore}
				<div bind:this={sentinel} class="infinite-sentinel" aria-hidden="true" />
			{/if}
		</div>
	</div>
</div>

<style>
	.all-promotions-wrapper {

		position: relative;

		display: flex;

		flex-direction: column;

		gap: 1rem
}

	.alternative-all-promotions-wrapper {

		overflow: hidden;

		padding: 0.125rem
}

	@media (min-width: 1280px) {
		.alternative-all-promotions-wrapper {

				padding: 1rem
		}
	}

	.circle {

		position: absolute;

		height: 300px;

		width: 200px;

		border-radius: 9999px;

		background-color: rgb(34 197 94 / 0.5);

		--tw-blur: blur(100px);

		filter: var(--tw-blur) var(--tw-brightness) var(--tw-contrast) var(--tw-grayscale) var(--tw-hue-rotate) var(--tw-invert) var(--tw-saturate) var(--tw-sepia) var(--tw-drop-shadow)
}

	.circle-1 {

		top: 10%;

		left: 30%
}

	.circle-2 {

		top: 25%;

		right: 15%
}

	.circle-3 {

		bottom: 20%;

		left: 10%
}

	.circle-4 {

		bottom: 10%;

		right: 25%
}

	.circle-5 {

		top: 75%;

		left: 20%;

		--tw-translate-x: -50%;

		--tw-translate-y: -50%;

		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))
}

	.top-content {

		display: flex;

		flex-direction: column;

		justify-content: space-between;

		gap: 1rem
}

	@media (min-width: 1280px) {
		.top-content {

				flex-direction: row;

				align-items: center;

				gap: 0px
		}
	}

	.title-container {

		display: flex;

		min-width: 30%;

		align-items: center;

		gap: 0.5rem
}

	.top-description {

		width: -moz-fit-content;

		width: fit-content;

		padding-left: 0px
}

	@media (min-width: 1024px) {
		.top-description {

				max-width: 500px;

				padding-left: 3rem
		}
	}

	.hot-promotions {

		margin-top: 0.5rem;

		display: grid;

		width: 100%;

		grid-template-columns: repeat(3, minmax(0, 1fr));

		gap: 1.5rem
}
	@media (min-width: 1441px) and (max-width: 1730px) {
		.hot-promotions-panels-open {

				grid-template-columns: repeat(2, minmax(0, 1fr))
		}
	}

	@media (max-width: 768px) {
		.hot-promotions {

				grid-template-columns: repeat(1, minmax(0, 1fr))
		}
	}

	@media (min-width: 768px) and (max-width: 1440px) {
		.hot-promotions {

				grid-template-columns: repeat(2, minmax(0, 1fr))
		}
	}

	.infinite-sentinel {

		height: 1rem;

		width: 100%
}
</style>
